import {EMPTY, of} from "rxjs"
import {AnyAction} from "redux"
import {isActionOf} from "typesafe-actions"
import {ActionsObservable, StateObservable} from "redux-observable"
import {catchError, filter, map, switchMap, withLatestFrom} from "rxjs/operators"

// Models
import {User} from "../../../auth/models"
import {DoctorStatsType} from "../../models"
import {DataResponse} from "../../../http/models"
import {EpicDependencies, RootState} from "../../../../models"

// Actions
import * as a from "../actions"

// Selectors
import {selectUser} from "../../../auth/store/selectors"
import {
    selectAvailableRecordsCurrentPage,
    selectAvailableRecordsPeriod,
    selectAvailableRecordsSize,
} from "../selectors"

// Utils
import {buildRecordsSearch} from "../../utils/dateRange"

/**
 * @param action$
 * @param state$
 * @param d
 */
export const fetchDoctorStatsEpic = (action$: ActionsObservable<AnyAction>, state$: StateObservable<RootState>, d: EpicDependencies) => {
    return action$.pipe(
        filter(isActionOf(a.fetchStatsAsync.request)),
        withLatestFrom(state$),
        switchMap(([, state]) => {
            const user = selectUser(state);

            if (!user) {
                return EMPTY
            }

            return d.doctorStatsService.fetchStats((user as User).id).pipe(
                map(response => {
                    const {data}: DataResponse<DoctorStatsType> = response.response;

                    return a.fetchStatsAsync.success(data)
                }),
                catchError(err => of(a.fetchStatsAsync.failure(err))),
            )
        }),
    )
};

/**
 * @param action$
 * @param state$
 * @param d
 */
export const fetchDoctorAvailableRecordsEpic = (action$: ActionsObservable<AnyAction>, state$: StateObservable<RootState>, d: EpicDependencies) => {
    return action$.pipe(
        filter(isActionOf([a.fetchAvailableRecordsAsync.request, a.setAvailableRecordsPage])),
        withLatestFrom(state$),
        switchMap(([, state]) => {
            const user = selectUser(state);
            const page = selectAvailableRecordsCurrentPage(state);
            const size = selectAvailableRecordsSize(state);
            const period = selectAvailableRecordsPeriod(state);

            // Sign-out unmounts the dashboard, whose cleanup restores the
            // Classic page size — so `setAvailableRecordsPage` can arrive after
            // `auth.user` is already null. Dereferencing it here threw inside
            // switchMap's project function, which RxJS turns into an error
            // notification; root-epic is a bare combineEpics with no
            // catchError, so that error tore down EVERY epic for the rest of
            // the session (sign-in included).
            if (!user) {
                return EMPTY
            }
            const search = buildRecordsSearch(period, page, size, new Date());

            return d.doctorStatsService.fetchAvailableRecords((user as User).id, search).pipe(
                map(response => {
                    const {data: {content, totalElements}} = response.response;

                    return a.fetchAvailableRecordsAsync.success({records: content, total: totalElements})
                }),
                catchError(err => of(a.fetchAvailableRecordsAsync.failure(err))),
            )
        }),
    )
};

/**
 * @param action$
 * @param state$
 * @param d
 */
export const fetchInProgressRecordEpic = (action$: ActionsObservable<AnyAction>, state$: StateObservable<RootState>, d: EpicDependencies) => {
    return action$.pipe(
        filter(isActionOf(a.fetchInProgressRecordAsync.request)),
        withLatestFrom(state$),
        switchMap(([, state]) => {
            const user = selectUser(state);

            if (!user) {
                return EMPTY
            }

            return d.doctorStatsService.fetchInProgress((user as User).id).pipe(
                map(response => {
                    const {data} = response.response;

                    if (typeof data === "undefined") {
                        return a.fetchInProgressRecordAsync.success(null)
                    }

                    return a.fetchInProgressRecordAsync.success(data)
                }),
                catchError(err => of(a.fetchInProgressRecordAsync.failure(err))),
            )
        }),
    )
};